import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ANALYTICS_LOGGING_API_CONFIG, AnalyticsLoggingConfig } from '../config';
import {
  EventQueryParams,
  UserActivityQueryParams,
  PerformanceMetricsQueryParams,
  AuditTrailQueryParams,
  PaginatedEventsResponse,
  OrganizationOverviewResponse,
  UserActivityResponse,
  SpecificUserActivityResponse,
  AuditTrailResponse,
  PerformanceMetricsResponse
} from '../models';

/**
 * Service for Organization Analytics
 * 
 * This service provides comprehensive methods for organization members to:
 * - View organization-scoped analytics and insights
 * - Track user activity within the organization
 * - Monitor performance metrics
 * - Access audit trails for resources
 * - Query organization events with advanced filtering
 * 
 * @remarks
 * - Requires authentication (Firebase JWT)
 * - Automatically scoped to user's organization
 * - May require premium subscription (controlled by backend)
 * - Rate limited to 100 requests per 15 minutes
 * 
 * @example
 * ```typescript
 * // Inject the service
 * private orgAnalytics = inject(OrganizationAnalyticsService);
 * 
 * // Get organization overview
 * this.orgAnalytics.getOverview({ hours: 24 }).subscribe(response => {
 *   console.log('Total events:', response.data.statistics.totalEvents);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationAnalyticsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ANALYTICS_LOGGING_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/analytics`;
    this.logDebug('OrganizationAnalyticsService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // ORGANIZATION OVERVIEW
  // ============================================================================

  /**
   * Get organization analytics overview
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of organization overview statistics
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getOverview({ hours: 24 }).subscribe(response => {
   *   const stats = response.data.statistics;
   *   console.log('Organization:', response.data.organizationId);
   *   console.log('Total events:', stats.totalEvents);
   *   console.log('Active users:', stats.activeUsers);
   * });
   * ```
   */
  getOverview(params?: { hours?: number }): Observable<OrganizationOverviewResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/overview`;
    
    this.logDebug('GET organization overview', { url, params });
    
    return this.http.get<OrganizationOverviewResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getOverview', error))
    );
  }

  // ============================================================================
  // ORGANIZATION EVENTS QUERY
  // ============================================================================

  /**
   * Query events within the organization with advanced filtering
   * 
   * Supports:
   * - Wildcard event types (e.g., 'blog_post.*')
   * - Comma-separated values (e.g., 'error,critical')
   * - Date ranges or hour-based filtering
   * - Pagination
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of paginated events
   * 
   * @example
   * ```typescript
   * // Get all blog post events in last 7 days
   * this.orgAnalytics.queryEvents({
   *   eventType: 'blog_post.*',
   *   hours: 168,
   *   page: 1,
   *   limit: 50
   * }).subscribe(response => {
   *   console.log('Events:', response.data.events);
   *   console.log('Total:', response.data.pagination.totalEvents);
   * });
   * 
   * // Get errors and warnings
   * this.orgAnalytics.queryEvents({
   *   severity: 'error,warn',
   *   hours: 24
   * }).subscribe(response => {
   *   console.log('Issues:', response.data.events);
   * });
   * ```
   */
  queryEvents(params?: EventQueryParams): Observable<PaginatedEventsResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/events`;
    
    this.logDebug('GET organization events', { url, params });
    
    return this.http.get<PaginatedEventsResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('queryEvents', error))
    );
  }

  // ============================================================================
  // USER ACTIVITY
  // ============================================================================

  /**
   * Get activity summary for all users in the organization
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of user activity data
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getUserActivity({ hours: 24 }).subscribe(response => {
   *   const users = response.data.users;
   *   users.forEach(user => {
   *     console.log(`${user.userEmail}: ${user.totalActions} actions`);
   *   });
   * });
   * ```
   */
  getUserActivity(params?: UserActivityQueryParams): Observable<UserActivityResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/user-activity`;
    
    this.logDebug('GET user activity', { url, params });
    
    return this.http.get<UserActivityResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getUserActivity', error))
    );
  }

  /**
   * Get detailed activity for a specific user
   * 
   * @param userId - User ID to query
   * @param params - Query parameters (optional)
   * @returns Observable of specific user activity
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getSpecificUserActivity('user_123', { hours: 48 }).subscribe(response => {
   *   console.log('User:', response.data.userEmail);
   *   console.log('Total events:', response.data.totalEvents);
   *   console.log('Recent events:', response.data.recentEvents);
   * });
   * ```
   */
  getSpecificUserActivity(userId: string, params?: UserActivityQueryParams): Observable<SpecificUserActivityResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/user/${userId}`;
    
    this.logDebug('GET specific user activity', { url, userId, params });
    
    return this.http.get<SpecificUserActivityResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getSpecificUserActivity', error))
    );
  }

  // ============================================================================
  // AUDIT TRAIL
  // ============================================================================

  /**
   * Get complete audit trail for a specific resource
   * 
   * @param resourceType - Type of resource (e.g., 'post', 'comment', 'user')
   * @param resourceId - Resource ID
   * @returns Observable of audit trail
   * 
   * @example
   * ```typescript
   * // Get audit trail for a blog post
   * this.orgAnalytics.getAuditTrail('post', 'post_123').subscribe(response => {
   *   console.log('Total events:', response.data.totalEvents);
   *   response.data.events.forEach(event => {
   *     console.log(`${event.timestamp}: ${event.eventType} by ${event.actor?.userName}`);
   *   });
   * });
   * 
   * // Get audit trail for a comment
   * this.orgAnalytics.getAuditTrail('comment', 'comment_456').subscribe(response => {
   *   console.log('Comment history:', response.data.events);
   * });
   * ```
   */
  getAuditTrail(resourceType: string, resourceId: string): Observable<AuditTrailResponse> {
    const url = `${this.apiUrl}/audit-trail/${resourceType}/${resourceId}`;
    
    this.logDebug('GET audit trail', { url, resourceType, resourceId });
    
    return this.http.get<AuditTrailResponse>(url).pipe(
      catchError(error => this.handleError('getAuditTrail', error))
    );
  }

  // ============================================================================
  // PERFORMANCE METRICS
  // ============================================================================

  /**
   * Get organization API usage and performance metrics
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of performance metrics
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getPerformanceMetrics({ hours: 24 }).subscribe(response => {
   *   const metrics = response.data.metrics;
   *   console.log('Total requests:', metrics.totalRequests);
   *   console.log('Avg response time:', metrics.averageResponseTime);
   *   console.log('Error rate:', metrics.errorRate);
   * });
   * ```
   */
  getPerformanceMetrics(params?: PerformanceMetricsQueryParams): Observable<PerformanceMetricsResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/performance`;
    
    this.logDebug('GET performance metrics', { url, params });
    
    return this.http.get<PerformanceMetricsResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getPerformanceMetrics', error))
    );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get recent errors in the organization
   * 
   * @param hours - Time range in hours (default: 24)
   * @returns Observable of error events
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getRecentErrors(48).subscribe(response => {
   *   console.log('Recent errors:', response.data.events);
   * });
   * ```
   */
  getRecentErrors(hours: number = 24): Observable<PaginatedEventsResponse> {
    return this.queryEvents({
      eventType: 'api.error,client.error',
      severity: 'error,critical',
      hours,
      limit: 100
    });
  }

  /**
   * Get security events in the organization
   * 
   * @param hours - Time range in hours (default: 24)
   * @returns Observable of security events
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getSecurityEvents(24).subscribe(response => {
   *   console.log('Security events:', response.data.events);
   * });
   * ```
   */
  getSecurityEvents(hours: number = 24): Observable<PaginatedEventsResponse> {
    return this.queryEvents({
      category: 'security',
      hours
    });
  }

  /**
   * Get blog post activity
   * 
   * @param hours - Time range in hours (default: 168 / 7 days)
   * @returns Observable of blog post events
   * 
   * @example
   * ```typescript
   * this.orgAnalytics.getBlogPostActivity(168).subscribe(response => {
   *   console.log('Blog post activity:', response.data.events);
   * });
   * ```
   */
  getBlogPostActivity(hours: number = 168): Observable<PaginatedEventsResponse> {
    return this.queryEvents({
      eventType: 'blog_post.*',
      hours
    });
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Build HTTP params from query parameters object
   */
  private buildHttpParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    
    return httpParams;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(method: string, error: any): Observable<never> {
    console.error(`OrganizationAnalyticsService.${method} error:`, error);
    
    let errorMessage = 'An error occurred while fetching analytics data';
    
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    if (error.status === 401) {
      errorMessage = 'Unauthorized. Please log in to access analytics.';
    } else if (error.status === 403) {
      errorMessage = 'This feature requires a premium subscription. Contact support to upgrade.';
    } else if (error.status === 404) {
      errorMessage = 'User not associated with any organization.';
    } else if (error.status === 429) {
      errorMessage = 'Rate limit exceeded. Please try again later.';
    }
    
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Debug logging
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[OrganizationAnalytics] ${message}`, data || '');
    }
  }
}
